/**
 * ContentPage — audience tabs, month cards, week rows, day buttons.
 *
 * Navigation hierarchy:
 *   Audience tabs (Mothers | Fathers | Grandparents)
 *   → Month cards (Month 1–9)
 *     → Week rows (Week 1–40)
 *       → Day buttons (Day 1–280) → /content/:audience/day/:dayNumber
 */

import { useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuth } from '@/hooks/useAuth';
import { useAudienceStatuses } from '@/hooks/useContent';
import {
  getAllMonths,
  getWeekDayRange,
  getMonthDayRange,
} from '@/config/pregnancyCalendar';
import { extractMonthProgress } from '@/services/contentService';
import { AUDIENCES, AUDIENCE_LABELS } from '@/types/auth.types';

const AUDIENCE_DOT: Record<string, string> = {
  mothers:      'bg-pink-400',
  fathers:      'bg-blue-400',
  grandparents: 'bg-purple-400',
};
import type { Audience } from '@/types/auth.types';
import type { ContentStatus } from '@/types/content.types';
import type { MonthInfo, WeekInfo } from '@/config/pregnancyCalendar';
import LoadingSpinner from '@/components/common/LoadingSpinner';

// ─── Status colours for day buttons ───────────────────────────────────────────

const STATUS_BG: Record<ContentStatus, string> = {
  not_started: 'bg-surface-100 text-surface-500 hover:bg-surface-200 border-surface-200',
  in_progress: 'bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-300',
  completed:   'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-300',
  published:   'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border-indigo-300',
};

// ─── Day button ────────────────────────────────────────────────────────────────

function DayButton({
  dayNumber,
  status,
  onClick,
}: {
  dayNumber: number;
  status: ContentStatus;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={`Day ${dayNumber} — ${status.replace('_', ' ')}`}
      className={cn(
        'w-9 h-9 rounded-lg border text-xs font-semibold transition-all',
        STATUS_BG[status],
      )}
    >
      {dayNumber}
    </button>
  );
}

// ─── Week row ──────────────────────────────────────────────────────────────────

function WeekRow({
  week,
  statusMap,
  onDayClick,
}: {
  week: WeekInfo;
  statusMap: Map<number, ContentStatus>;
  onDayClick: (dayNumber: number) => void;
}) {
  const days: number[] = [];
  for (let d = week.dayStart; d <= week.dayEnd; d++) {
    days.push(d);
  }

  const completedCount = days.filter(d => {
    const s = statusMap.get(d) ?? 'not_started';
    return s === 'completed' || s === 'published';
  }).length;

  return (
    <div className="flex items-center gap-4 py-2.5 px-4 hover:bg-surface-50 rounded-lg">
      {/* Week label */}
      <div className="w-20 shrink-0">
        <p className="text-xs font-semibold text-surface-700">Week {week.weekNumber}</p>
        <p className="text-[10px] text-surface-400">{getWeekDayRange(week.weekNumber)}</p>
      </div>

      {/* Day buttons */}
      <div className="flex gap-1.5 flex-wrap">
        {days.map(d => (
          <DayButton
            key={d}
            dayNumber={d}
            status={statusMap.get(d) ?? 'not_started'}
            onClick={() => onDayClick(d)}
          />
        ))}
      </div>

      {/* Week count */}
      <div className="ml-auto text-[10px] text-surface-400 shrink-0">
        {completedCount}/{days.length}
      </div>
    </div>
  );
}

// ─── Month card ────────────────────────────────────────────────────────────────

function MonthCard({
  month,
  statusMap,
  onDayClick,
}: {
  month: MonthInfo;
  statusMap: Map<number, ContentStatus>;
  onDayClick: (dayNumber: number) => void;
}) {
  const [open, setOpen] = useState(false);

  // Calculate month progress
  const days = extractMonthProgress(statusMap, month.dayStart, month.dayEnd);
  const total     = days.length;
  const completed = days.filter(d => d.status === 'completed' || d.status === 'published').length;
  const inProg    = days.filter(d => d.status === 'in_progress').length;
  const pct       = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="rounded-xl border border-surface-200 bg-white overflow-hidden shadow-sm">
      {/* Header — click to toggle */}
      <button
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-surface-50 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        {/* Toggle icon */}
        <span className="text-surface-400 shrink-0">
          {open
            ? <ChevronDown className="h-4 w-4" />
            : <ChevronRight className="h-4 w-4" />
          }
        </span>

        {/* Month number badge */}
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-primary-700 font-bold text-sm shrink-0">
          M{month.monthNumber}
        </span>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-surface-900">
            Month {month.monthNumber}
          </p>
          <p className="text-xs text-surface-400">
            Weeks {month.weekStart}–{month.weekEnd} · {getMonthDayRange(month.monthNumber)}
          </p>
        </div>

        {/* Progress */}
        <div className="text-right shrink-0">
          <p className="text-sm font-semibold text-surface-900">{pct}%</p>
          <p className="text-[10px] text-surface-400">
            {completed} done · {inProg} in progress
          </p>
        </div>
      </button>

      {/* Progress bar */}
      <div className="h-1 bg-surface-100">
        <div
          className="h-1 bg-emerald-400 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Week rows — visible when expanded */}
      {open && (
        <div className="divide-y divide-surface-100 border-t border-surface-100 p-2">
          {month.weeks.map((week: WeekInfo) => (
            <WeekRow
              key={week.weekNumber}
              week={week}
              statusMap={statusMap}
              onDayClick={onDayClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── ContentPage ───────────────────────────────────────────────────────────────

export default function ContentPage() {
  const navigate   = useNavigate();
  const { cmsUser, isAdmin } = useAuth();

  // Determine initial audience from URL or default
  const { audience: urlAudience } = useParams<{ audience?: string }>();
  const validAudiences = AUDIENCES as readonly string[];
  const initialAudience: Audience =
    urlAudience && validAudiences.includes(urlAudience)
      ? (urlAudience as Audience)
      : 'mothers';

  const [activeAudience, setActiveAudience] = useState<Audience>(initialAudience);
  const [goToDay, setGoToDay]               = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Load all day statuses for the active audience
  const { data: statusMap, isLoading } = useAudienceStatuses(activeAudience);
  const safeMap = statusMap ?? new Map<number, ContentStatus>();

  const months = getAllMonths();

  // Overall audience stats
  const completedTotal = [...safeMap.values()].filter(
    s => s === 'completed' || s === 'published',
  ).length;
  const inProgTotal = [...safeMap.values()].filter(
    s => s === 'in_progress',
  ).length;

  const handleAudienceChange = (a: Audience) => {
    setActiveAudience(a);
    navigate(`/content/${a}`, { replace: true });
  };

  const handleDayClick = (dayNumber: number) => {
    navigate(`/content/${activeAudience}/day/${dayNumber}`);
  };

  const handleGoToDay = () => {
    const n = parseInt(goToDay, 10);
    if (n >= 1 && n <= 280) {
      handleDayClick(n);
    }
  };

  // Admins bypass all permission checks
  const accessible = isAdmin || cmsUser?.permissions[activeAudience]?.view !== false;

  return (
    <div className="space-y-6">

      {/* ── Page header ──────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-surface-900">Content Management</h2>
          <p className="text-sm text-surface-500 mt-0.5">
            280 days · 40 weeks · 9 months · 3 audiences
          </p>
        </div>

        {/* Go-to-day */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <input
              ref={inputRef}
              type="number"
              min={1}
              max={280}
              value={goToDay}
              onChange={e => setGoToDay(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleGoToDay()}
              placeholder="Go to day…"
              className="w-36 rounded-lg border border-surface-200 bg-white px-3 py-2 text-sm
                         text-surface-900 placeholder:text-surface-400
                         focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={handleGoToDay}
            className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2
                       text-sm font-medium text-white hover:bg-primary-700 transition-colors"
          >
            <Search className="h-3.5 w-3.5" />
            Go
          </button>
        </div>
      </div>

      {/* ── Audience tabs ─────────────────────────────────────────────── */}
      <div className="flex gap-1 p-1 rounded-xl bg-surface-100 w-fit">
        {AUDIENCES.map(aud => {
          const isActive = aud === activeAudience;
          return (
            <button
              key={aud}
              onClick={() => handleAudienceChange(aud)}
              className={cn(
                'px-5 py-2 rounded-lg text-sm font-semibold transition-all',
                isActive
                  ? 'bg-white shadow-sm text-surface-900'
                  : 'text-surface-500 hover:text-surface-700',
              )}
            >
              <span className={cn('inline-block w-2 h-2 rounded-full mr-2', AUDIENCE_DOT[aud])} />
              {AUDIENCE_LABELS[aud]}
            </button>
          );
        })}
      </div>

      {/* ── Audience summary bar ───────────────────────────────────────── */}
      <div className="flex items-center gap-6 px-5 py-3 rounded-xl border border-surface-200 bg-white">
        {isLoading ? (
          <LoadingSpinner size="sm" />
        ) : (
          <>
            <Stat label="Completed" value={completedTotal}color="text-emerald-600" />
            <div className="w-px h-6 bg-surface-200" />
            <Stat label="In Progress" value={inProgTotal}color="text-amber-600" />
            <div className="w-px h-6 bg-surface-200" />
            <Stat label="Not Started" value={280 - completedTotal - inProgTotal}color="text-surface-500" />
            <div className="ml-auto w-48">
              <div className="flex justify-between text-xs text-surface-500 mb-1">
                <span>{Math.round((completedTotal / 280) * 100)}% complete</span>
                <span>{completedTotal}/280</span>
              </div>
              <div className="h-2 rounded-full bg-surface-100">
                <div
                  className="h-2 rounded-full bg-emerald-400 transition-all"
                  style={{ width: `${(completedTotal / 280) * 100}%` }}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── No access ─────────────────────────────────────────────────── */}
      {!accessible && (
        <div className="rounded-xl border border-surface-200 bg-white p-10 text-center">
          <p className="text-sm font-medium text-surface-500">
            You don't have access to {AUDIENCE_LABELS[activeAudience]} content.
          </p>
        </div>
      )}

      {/* ── Month cards ───────────────────────────────────────────────── */}
      {accessible && (
        <div className="grid grid-cols-1 gap-3">
          {months.map(month => (
            <MonthCard
              key={month.monthNumber}
              month={month}
              statusMap={safeMap}
              onDayClick={handleDayClick}
            />
          ))}
        </div>
      )}

      {/* ── Legend ───────────────────────────────────────────────────── */}
      <div className="flex items-center gap-5 pt-2">
        <p className="text-xs text-surface-400 font-medium">Day status:</p>
        {(
          [
            { status: 'not_started', label: 'Not Started' },
            { status: 'in_progress', label: 'In Progress' },
            { status: 'completed',   label: 'Completed'   },
            { status: 'published',   label: 'Published'   },
          ] as { status: ContentStatus; label: string }[]
        ).map(({ status, label }) => (
          <div key={status} className="flex items-center gap-1.5">
            <span className={cn('w-5 h-5 rounded border text-[9px] flex items-center justify-center font-bold', STATUS_BG[status])}>
              1
            </span>
            <span className="text-xs text-surface-500">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Tiny stat widget ──────────────────────────────────────────────────────────

function Stat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div>
      <p className={cn('text-lg font-bold', color)}>{value}</p>
      <p className="text-xs text-surface-400">{label}</p>
    </div>
  );
}
